import { PermissionType } from '@/graphql/generated/types.generated';
import type { MappedFeature } from './map-graphql-to-calculator.util';

/**
 * Clamps a raw numeric add-on quantity (the extra amount above the plan's included base)
 * to a valid value: never negative, never above `max − base`, and snapped up to the next
 * multiple of the add-on step. Mirrors the inline logic used by the add-on stepper input.
 */
export function clampNumericAddOn(raw: number, feature: MappedFeature): number {
    const step = feature.addOnStep ?? 1;
    const absoluteMax = feature.addOnMax ?? 1_000_000;
    const max = Math.max(0, absoluteMax - feature.value);
    const clamped = Math.max(0, Number.isFinite(raw) ? raw : 0);
    const snapped = clamped === 0 ? 0 : Math.ceil(clamped / step) * step;
    return Math.min(max, snapped);
}

/** Seeded, already-validated add-on state, ready to drop into the calculator hook. */
export interface ResolvedAddOns {
    qty: Record<string, number>;
    booleans: Record<string, boolean>;
    enums: Record<string, string>;
}

const isTruthy = (raw: string | number | boolean): boolean =>
    raw === true || raw === 1 || raw === 'true' || raw === '1';

const toNumber = (raw: string | number | boolean): number =>
    typeof raw === 'number' ? raw : parseInt(String(raw), 10);

/**
 * Normalises a permission code for lookups so external inputs can be written in any case and
 * with either separator: `PROFILES_GROUPS`, `profiles-groups`, `Profiles_Groups` all match.
 * (The canonical `PermissionCode` values are UPPER_SNAKE; URLs typically use lowercase-kebab.)
 */
const normalizeCode = (code: string): string => code.toLowerCase().replace(/_/g, '-');

/**
 * Whether an add-on feature is "active" — either included in the base plan or selected via
 * the seeded add-on maps. Used to enforce dependencies (a child add-on is only valid when
 * its parent is active).
 */
function isFeatureActive(feature: MappedFeature, seeded: ResolvedAddOns): boolean {
    switch (feature.type) {
        case PermissionType.Boolean:
            return feature.value === 1 || seeded.booleans[feature.code] === true;
        case PermissionType.Numeric:
            return feature.value > 0 || (seeded.qty[feature.code] ?? 0) > 0;
        case PermissionType.Enum: {
            const seededEnum = seeded.enums[feature.code];
            return feature.displayValue !== '' || (!!seededEnum && seededEnum !== '');
        }
        default:
            return false;
    }
}

/**
 * Turns an externally-supplied `code → value` map (from a prop or URL query params, so values
 * may be strings) into clamped, validated add-on state for the given plan's features.
 *
 * - Numeric: coerced to a number and clamped to `[0, max − base]`, snapped to the step; kept only if > 0.
 * - Boolean: kept only when enabled AND not already included in the base plan.
 * - Enum: kept only when it's a real option AND a strict upgrade over the base tier
 *   (options come pre-sorted ascending, so a higher index = higher tier).
 * - Codes not offered for this plan are ignored.
 * - Finally, any add-on whose `dependsOn` parent is not active is dropped.
 *
 * Input keys and enum values are matched case-insensitively and accept either separator
 * (`profiles-groups` ≡ `PROFILES_GROUPS`); the resolved state always uses the canonical codes.
 */
export function resolveInitialAddOns(
    features: MappedFeature[],
    initial: Record<string, string | number | boolean> | undefined,
): ResolvedAddOns {
    const result: ResolvedAddOns = { qty: {}, booleans: {}, enums: {} };
    if (!initial) return result;

    const byCode = new Map(features.map((f) => [normalizeCode(f.code), f]));

    for (const [rawKey, raw] of Object.entries(initial)) {
        const feature = byCode.get(normalizeCode(rawKey));
        if (!feature || raw == null) continue;
        const code = feature.code; // canonical

        switch (feature.type) {
            case PermissionType.Numeric: {
                const value = clampNumericAddOn(toNumber(raw), feature);
                if (value > 0) result.qty[code] = value;
                break;
            }
            case PermissionType.Boolean: {
                if (isTruthy(raw) && feature.value !== 1) result.booleans[code] = true;
                break;
            }
            case PermissionType.Enum: {
                const options = feature.addOnOptions ?? [];
                const target = normalizeCode(String(raw));
                const optionIndex = options.findIndex((o) => normalizeCode(o.value) === target);
                if (optionIndex < 0) break; // not a real option
                // strict upgrade over the base tier (baseIndex = -1 when nothing is included)
                const baseIndex = options.findIndex((o) => o.value === feature.displayValue);
                if (optionIndex > baseIndex) result.enums[code] = options[optionIndex].value; // canonical
                break;
            }
            default:
                break;
        }
    }

    // Dependency pass: drop any seeded add-on whose required parent is not active.
    for (const feature of features) {
        if (!feature.dependsOn) continue;
        const isSeeded =
            feature.code in result.qty ||
            feature.code in result.booleans ||
            feature.code in result.enums;
        if (!isSeeded) continue;

        // Only drop when the parent IS a purchasable add-on but is inactive. A parent that
        // isn't offered as an add-on (e.g. `profiles` for brand accounts, which is included in
        // the base plan) can't be judged inactive here — assume active; the backend validates.
        const parent = byCode.get(normalizeCode(feature.dependsOn));
        if (parent && !isFeatureActive(parent, result)) {
            delete result.qty[feature.code];
            delete result.booleans[feature.code];
            delete result.enums[feature.code];
        }
    }

    return result;
}
